// Layer 3 (Transformations): the Superintendent landing (polish.P25 #712,
// locked comp `.impeccable/mocks/decision/landing-superintendent.html` C with
// A's views 1–3). The strip's four cells, the Daily meeting card, the week
// matrix, the Panel B and SMBS gates, the cost bars, the plan figure row and
// the cumulative chart's geometry. Every figure reads an existing function
// (heat-map rows, GmMoney, the plan read, the meeting register); nothing here
// types a number of its own.

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { VizTone } from '@/versions/v1/modules/_viz';
import { formatPtDelta } from '@/versions/v1/shared/recovery-vocab.derive';
import { smelterPenaltyLimit } from '@/versions/v1/shared/bunker-hill-smelter-terms';
import { BUNKER_HILL_ASSIGNMENTS, BUNKER_HILL_PERSONS } from '@/versions/v1/shared/bunker-hill-org-seed';
import { productionDayOf } from '@/versions/v1/shared/clock.derive';
import { meetingKpis, openThreeWs, threeWStatus } from '@/versions/v1/modules/meeting/Scene.transform';
import type { EscalationDecisionState } from '@/versions/v1/shared/EscalationDecisionEngine';
import { gmAmount, gmMoney } from './GmMoney.transform';
import { escalationCards } from './GmTabs.transform';
import type { GmMiniItem, LandingStripCell } from './GmLeadBand.transform';
import { heatStyle, heatTargets, panelBNightPattern, shiftHeatRows, shiftSummaryRead, type ShiftHeatRow } from './ShiftHeatmap.transform';
import {
  costVarianceTone,
  gapClosingOptions,
  issuesByRoleColumns,
  mtdCostBreakdownRows,
  mtdCostPerTonne,
  mtdCostTotal,
  mtdCumulativeSeries,
  mtdRevenueEbitdaRows,
  type CostRow,
} from './SuptCostsIssuesPlan.transform';
import { SMBS_DOSE_PCT } from '@/versions/v1/seed/bunker-hill-issue-spine';
import { formatDateOffsetFromCursor, issuesBoardTitleStamp, shiftValueAt } from './LandingCursor.transform';
import { superintendentPlanFigures } from './RoleLandingTabKpis.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

export type SuptTabId = 'shifts' | 'costs' | 'issues' | 'plan';

/** The page subtitle under the Overview title (the former section heading). */
export const SUPT_SUBTITLE = 'Shift / weekly / MTD performance vs target, budget and plan';

/** The seat's short name on chips and stamps. The gates themselves are gated by
 *  capability (`escalation:decide`), never by the persona's display name. */
export const SUPT_SEAT_LABEL = 'Ops Supt';

/** Record ids in `EscalationDecisionEngine` for the landing's two gates. */
export const SUPT_GATE = { panelB: 'supt-panel-b', smbs: 'supt-smbs' } as const;

/** The SMBS review's board item: the Control met proposal that waits on this seat. */
const SMBS_ITEM_ID = 'ibr-6';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-06-08T11:30` → `08 Jun 11:30`, the gates' stamp spelling. */
export function stampOf(at: string): string {
  const [date, time] = at.split('T');
  const [, month, day] = (date ?? '').split('-');
  return `${day} ${MONTHS[Number(month) - 1]} ${time}`;
}

/** `8 Jun` → `08 Jun`, the gates' date-field spelling. */
function padDay(label: string): string {
  return label.replace(/^(\d) /, '0$1 ');
}

function seatHolderName(seatId: string): string {
  const personId = BUNKER_HILL_ASSIGNMENTS.find((row) => row.seatId === seatId)?.personId;
  const name = BUNKER_HILL_PERSONS.find((row) => row.id === personId)?.name;
  if (!name) throw new Error(`Seat landing: no holder for ${seatId}`);
  return name;
}

// ---- The issues board, with the SMBS approval applied -----------------------

export type SuptIssueStatus =
  | { kind: 'pill'; label: string; tone: 'ok' | 'warn' | 'neutral' }
  | { kind: 'draft' }
  | { kind: 'approved'; label: string };

export type SuptIssueItem = {
  id: string;
  rank: number;
  title: string;
  detail: string;
  priority: 'HIGH' | 'MED';
  status: SuptIssueStatus;
};

export type SuptIssueColumn = { id: string; roleLabel: string; items: readonly SuptIssueItem[] };

function approvalStamp(records: EscalationDecisionState): string | undefined {
  const record = records.get(SUPT_GATE.smbs);
  return record ? `Approved · ${stampOf(record.recordedAt)} · ${SUPT_SEAT_LABEL}` : undefined;
}

export function suptIssueColumns(state: SessionState, records: EscalationDecisionState): SuptIssueColumn[] {
  const approved = approvalStamp(records);
  return issuesByRoleColumns(state).map((col) => ({
    id: col.id,
    // Chips set in caps; the label keeps sentence case (comp `.actor`).
    roleLabel: col.roleLabel.charAt(0) + col.roleLabel.slice(1).toLowerCase(),
    items: col.items.map((item, index) => {
      let status: SuptIssueStatus;
      if (item.status.startsWith('Draft')) {
        status = approved ? { kind: 'approved', label: approved } : { kind: 'draft' };
      } else {
        status = {
          kind: 'pill',
          label: item.status,
          tone: item.status === 'Active' ? 'ok' : item.status === 'In progress' ? 'warn' : 'neutral',
        };
      }
      return { id: item.id, rank: index + 1, title: item.title, detail: item.detail, priority: item.priority, status };
    }),
  }));
}

export function suptIssueCounts(state: SessionState, records: EscalationDecisionState) {
  const items = suptIssueColumns(state, records).flatMap((col) => col.items);
  return {
    total: items.length,
    high: items.filter((item) => item.priority === 'HIGH').length,
    awaiting: items.filter((item) => item.status.kind === 'draft').length,
  };
}

/** The board card's meta: the scope and the cursor stamp (`top 3 each · 08 Jun · Day shift · 11:30`). */
export function suptIssuesMeta(state: SessionState): string {
  return `top 3 each · ${issuesBoardTitleStamp(state)}`;
}

// ---- The strip ------------------------------------------------------------------

export function suptStripCells(state: SessionState, records: EscalationDecisionState): LandingStripCell<SuptTabId>[] {
  const panelB = panelBNightPattern(state);
  const cost = gmMoney().mtd.cost;
  const over = cost.actual - cost.target;
  const counts = suptIssueCounts(state, records);
  const plan = superintendentPlanFigures(state);
  const gap = plan.mtdActual - plan.mtdPlan;
  return [
    {
      id: 'shifts',
      name: '7-day shifts',
      figure: `${panelB.panelBAvg.toFixed(1)}%`,
      figureContext: 'Panel B nights',
      comparisonLead: `Zn rec vs ${panelB.otherAvg.toFixed(1)}%`,
      comparisonRest: ` on other shifts · ${formatPtDelta(panelB.panelBAvg - panelB.otherAvg)}`,
      actor: 'Engine',
    },
    {
      id: 'costs',
      name: 'MTD costs',
      figure: gmAmount(cost.actual),
      figureContext: '1–7 Jun',
      comparisonLead: `vs Budget ${gmAmount(cost.target)}`,
      comparisonRest: ' · ',
      comparisonFlag: {
        text: `${gmAmount(Math.abs(over))} ${over > 0 ? 'over' : 'under'}`,
        tone: costVarianceTone({ id: 'c', label: '', budget: cost.target, actual: cost.actual }),
      },
      actor: 'Engine',
    },
    {
      id: 'issues',
      name: 'Issues by role',
      figure: String(counts.total),
      figureContext: 'active',
      comparisonLead: `${counts.high} high`,
      comparisonRest: ` · ${counts.awaiting} awaiting your approval`,
      actor: 'Human',
      actorLabel: SUPT_SEAT_LABEL,
    },
    {
      id: 'plan',
      name: 'Production vs plan',
      figure: plan.mtdActual.toLocaleString('en-US'),
      figureContext: 'dst MTD',
      comparisonLead: `vs budget ${plan.mtdPlan.toLocaleString('en-US')} dst`,
      comparisonRest: ` · ${gap >= 0 ? '+' : '−'}${Math.abs(gap).toLocaleString('en-US')} · ${Math.abs(plan.mtdPct).toFixed(1)}% ${gap >= 0 ? 'ahead' : 'behind'}`,
      actor: 'Engine',
    },
  ];
}

// ---- The Daily meeting card ------------------------------------------------

export type SuptMeetingCard = { open: number; overdue: number; aged: number; cccToday: number };

/** The meeting register at the cursor: open 3Ws, those past their committed
 * date, those aged over 14 days and the CCC reviews due today. */
export function suptMeetingCard(): SuptMeetingCard {
  const kpis = meetingKpis();
  return {
    open: kpis.openThreeWCount,
    overdue: openThreeWs().filter((row) => threeWStatus(row) === 'overdue').length,
    aged: kpis.agedOver14dCount,
    cccToday: kpis.cccRequiredTodayCount,
  };
}

// ---- 7-day shifts: the week matrix ----------------------------------------------

export type WeekMatrixColumn = {
  id: string;
  shift: 'D' | 'N';
  panel: ShiftHeatRow['panel'];
  panelBNight: boolean;
  /** First column of its production day: a hairline divides the days. */
  dayStart: boolean;
};

export type WeekMatrixDate = { label: string; span: number };

export type WeekMatrixCell = { value: string; tone: 'warn' | 'bad' | null };

export type WeekMatrixRow = { id: string; label: string; target?: string; cells: readonly WeekMatrixCell[] };

export type WeekMatrix = {
  dates: readonly WeekMatrixDate[];
  columns: readonly WeekMatrixColumn[];
  rows: readonly WeekMatrixRow[];
  /** The header line: the mini-row facts folded into one line (comp C). */
  lead: string;
  rest: string;
};

/**
 * The 14 completed shifts on their side: days across, metrics down (comp C).
 * Tone is text colour and only exceptions take it — the in-band cells stay
 * ink (status walls are quiet). Grade rows carry no target, so no tone.
 */
export function weekMatrix(state: SessionState): WeekMatrix {
  const rows = shiftHeatRows(state);
  const targets = heatTargets(state);
  const summary = shiftSummaryRead(state);
  const dates: WeekMatrixDate[] = [];
  const columns = rows.map((row, index) => {
    const dayStart = index === 0 || rows[index - 1]!.date !== row.date;
    if (dayStart) dates.push({ label: row.date, span: 1 });
    else dates[dates.length - 1]!.span += 1;
    return {
      id: row.id,
      shift: row.shift,
      panel: row.panel,
      panelBNight: row.panel === 'B' && row.shift === 'N',
      dayStart,
    };
  });
  const toned = (value: number, target: { target: number; tolerance: number } | null, decimals: number): WeekMatrixCell => {
    const tone = target ? heatStyle(value, target.target, target.tolerance) : null;
    return {
      value: decimals === 0 ? Math.round(value).toLocaleString('en-US') : value.toFixed(decimals),
      tone: tone === 'warn' || tone === 'bad' ? tone : null,
    };
  };
  const metric = (
    id: string,
    label: string,
    pick: (row: ShiftHeatRow) => number,
    target: { target: number; tolerance: number } | null,
    decimals: number,
  ): WeekMatrixRow => ({
    id,
    label,
    target: target ? (decimals === 0 ? String(target.target) : target.target.toFixed(1)) : undefined,
    cells: rows.map((row) => toned(pick(row), target, decimals)),
  });
  const best = summary.best;
  return {
    dates,
    columns,
    rows: [
      metric('tonnes', 'Tonnes', (r) => r.tonnes, targets.tonnes, 0),
      metric('pb-rec', 'Pb rec %', (r) => r.pbRec, targets.pbRec, 1),
      metric('zn-rec', 'Zn rec %', (r) => r.znRec, targets.znRec, 1),
      metric('pb-gr', 'Pb gr %', (r) => r.pbGrade, null, 1),
      metric('zn-gr', 'Zn gr %', (r) => r.znGrade, null, 1),
      metric('mill-util', 'Mill util %', (r) => r.millUtil, targets.millUtil, 1),
    ],
    lead: `Nights ${summary.nightAvgPct.toFixed(1)}% vs days ${summary.dayAvgPct.toFixed(1)}%`,
    rest: ` Zn rec · best: ${best.date} ${best.shift === 'D' ? 'day' : 'night'}, Panel ${best.panel}, ${best.znRec.toFixed(1)}% · ${summary.atTarget} of ${summary.shiftCount} at the ${summary.znRecTarget.toFixed(1)}% target`,
  };
}

// ---- 7-day shifts: the Panel B card and the SMBS approval --------------------

export type SuptGateModel = {
  id: string;
  lead: string;
  text: string;
  option: string;
  seats: readonly { role: string; holder: string }[];
  date: string;
  buttonLabel: string;
};

export type PanelBCardModel = {
  review: string;
  count: string;
  rest: string;
  hypotheses: readonly string[];
  gate: SuptGateModel;
};

/** Adjudication 2: only what the rows support — the Panel B nights' share of
 * the week's lowest Zn recovery shifts; the pair of averages sits in the strip.
 * The review date, hypotheses and owner are the GM's Panel B card's own. */
export function panelBCard(state: SessionState): PanelBCardModel {
  const pattern = panelBNightPattern(state);
  const card = escalationCards(state).find((row) => row.id === 'esc-4');
  if (!card) throw new Error('Seat landing: the Panel B escalation card is missing');
  return {
    review: `Review ${card.due}`,
    count: `${pattern.panelBNightInWorst} of the ${pattern.worstCount}`,
    rest: ' lowest Zn recovery shifts this week, all Panel B nights',
    hypotheses: card.hypotheses ?? [],
    gate: {
      id: SUPT_GATE.panelB,
      lead: 'Your next step:',
      text: 'a structured conversation with the Panel B shift supervisor and the Met team — not further data analysis',
      option: 'Hold conversation',
      seats: [{ role: 'Owns', holder: seatHolderName('seat-supt') }],
      date: card.date,
      buttonLabel: 'Record decision',
    },
  };
}

export type SmbsCardModel = {
  title: string;
  pbInZn: string;
  limit: string;
  priority: 'HIGH' | 'MED';
  gate: SuptGateModel;
};

/** The one approval that waits on this seat: the Control met SMBS proposal on
 * the Issues board. No Advisory case owns it, so the gate is landing-local. */
export function smbsCard(state: SessionState): SmbsCardModel {
  const item = issuesByRoleColumns(state).flatMap((col) => col.items).find((row) => row.id === SMBS_ITEM_ID);
  if (!item) throw new Error('Seat landing: the SMBS review item is missing');
  const pbInZn = shiftValueAt('rs-spec-zn-pb', state.cursor, state);
  if (pbInZn === undefined) throw new Error('Seat landing: no Pb-in-Zn reading at the cursor');
  return {
    title: item.title,
    pbInZn: `${pbInZn.toFixed(1)}%`,
    limit: `${smelterPenaltyLimit('Zn', 'Pb').toFixed(1)}%`,
    priority: item.priority,
    gate: {
      id: SUPT_GATE.smbs,
      lead: 'Your approval:',
      text: `+${SMBS_DOSE_PCT}% SMBS dose on the Zn circuit, before action`,
      option: 'Approve dose',
      seats: [
        { role: 'Proposes', holder: seatHolderName('seat-met') },
        { role: 'Approves', holder: SUPT_SEAT_LABEL },
      ],
      date: padDay(formatDateOffsetFromCursor(state, 0)),
      buttonLabel: 'Approve',
    },
  };
}

/** A recorded gate's stamp line (`Recorded · 08 Jun 11:30 · Ops Supt`). */
export function suptRecordLine(records: EscalationDecisionState, id: string): string | undefined {
  const record = records.get(id);
  if (!record) return undefined;
  return `${id === SUPT_GATE.smbs ? 'Approved' : 'Recorded'} · ${stampOf(record.recordedAt)} · ${SUPT_SEAT_LABEL}`;
}

/** The SMBS pill: the draft until the seat approves, then the approval stamp. */
export function smbsApprovalStamp(records: EscalationDecisionState): string | undefined {
  return approvalStamp(records);
}

/** The Waiting-on-you meta: the one approval still open once the seat approves. */
export function waitingApprovalLabel(approved: string | undefined): string {
  const waiting = approved ? 0 : 1;
  return `${waiting} approval${waiting === 1 ? '' : 's'}`;
}

// ---- MTD costs ------------------------------------------------------------------

export type CostBarRow = CostRow & {
  variance: string;
  tone: 'ok' | 'warn' | 'bad';
  /** Over-budget share of the largest overrun, 0–100; 0 when on or under budget. */
  barPct: number;
  total: boolean;
};

export function signedThousands(value: number): string {
  return `${value >= 0 ? '+' : '−'}${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function costBarRows(): CostBarRow[] {
  const rows = mtdCostBreakdownRows();
  const total = mtdCostTotal(rows);
  const maxOver = Math.max(...rows.map((row) => Math.max(0, row.actual - row.budget)), 0);
  const withBars = rows.map((row) => {
    const over = Math.max(0, row.actual - row.budget);
    return {
      ...row,
      variance: signedThousands(row.actual - row.budget),
      tone: costVarianceTone(row),
      barPct: maxOver > 0 ? (over / maxOver) * 100 : 0,
      total: false,
    };
  });
  return [
    ...withBars,
    { ...total, variance: signedThousands(total.actual - total.budget), tone: costVarianceTone(total), barPct: 0, total: true },
  ];
}

export function formatThousands(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export type RevenueRow = CostRow & {
  /** The signed variance as the glass prints it (`+389.53`), from the same
   *  subtract-and-format rule the cost table's rows use. */
  variance: string;
  tone: 'ok' | 'warn' | 'bad';
  /** The operating-EBITDA row: separated by a hairline, figures in ink. */
  total: boolean;
};

/** The revenue & operating-EBITDA table's rows. Every derived field the glass
 *  prints — the variance string, its tone, which row is the total — is decided
 *  here, so the TSX draws strings and never computes one. The tone reads the
 *  row's own comparator kind (revenue rows reverse `gmBudgetTone`). */
export function revenueRows(): RevenueRow[] {
  const rows = mtdRevenueEbitdaRows();
  return rows.map((row, index) => ({
    ...row,
    variance: signedThousands(row.actual - row.budget),
    tone: costVarianceTone(row, row.varianceKind ?? 'cost'),
    total: index === rows.length - 1,
  }));
}

/** The cost per short ton line under the cost table (was the fourth tile). */
export function costPerTonneLine(state: SessionState): { actual: string; budget: string; delta: string; tone: 'ok' | 'warn' } {
  const cpt = mtdCostPerTonne(state);
  return {
    actual: `${CURRENCY_GLYPH}${cpt.actualPerTonne.toFixed(1)}/dst`,
    budget: `vs ${CURRENCY_GLYPH}${cpt.budgetPerTonne.toFixed(1)}/dst budget at budget tonnes`,
    delta: `${cpt.deltaPerTonne >= 0 ? '+' : '−'}${CURRENCY_GLYPH}${Math.abs(cpt.deltaPerTonne).toFixed(1)}/dst`,
    tone: cpt.deltaPerTonne > 5 ? 'warn' : 'ok',
  };
}

// ---- Production vs plan ------------------------------------------------------

/** The three figures beside the strip's MTD cell (tonnes read "vs budget" first). */
export function suptPlanMiniRow(state: SessionState): GmMiniItem[] {
  const f = superintendentPlanFigures(state);
  const wtdGap = f.wtdOre - f.wtdPlan;
  const sign = (n: number) => (n >= 0 ? '+' : '−');
  const tone = (ok: boolean): VizTone => (ok ? 'ok' : 'warn');
  return [
    {
      id: 'supt-plan-wtd',
      label: 'WTD actual',
      value: `${f.wtdOre.toLocaleString('en-US')} dst`,
      captionLead: `vs budget ${f.wtdPlan.toLocaleString('en-US')} dst`,
      caption: ` · ${sign(wtdGap)}${Math.abs(wtdGap).toLocaleString('en-US')}`,
      tone: wtdGap >= 0 ? 'ok' : 'bad',
    },
    {
      id: 'supt-plan-forecast',
      label: 'Full-month forecast',
      value: `${f.forecast.toLocaleString('en-US')} dst`,
      captionLead: `vs budget ${f.monthPlan.toLocaleString('en-US')} dst`,
      caption: ' · straight MTD run-rate',
      tone: tone(f.forecast >= f.monthPlan),
    },
    {
      id: 'supt-plan-rate',
      label: 'Required rate to close',
      value: `${f.requiredRate.toLocaleString('en-US')} dst/day`,
      captionLead: `vs run-rate ${f.runRate.toLocaleString('en-US')} dst/day`,
      caption: ` · ${f.daysRemaining} days remaining`,
      tone: tone(f.requiredRate <= f.runRate),
    },
  ];
}

export type CumulativeChartModel = {
  /** Tick labels on the day axis (`1 Jun` · `2` … `7 Jun`). */
  days: readonly string[];
  actual: readonly number[];
  budget: readonly number[];
  yTicks: readonly number[];
  yMax: number;
  period: string;
  actualLabel: string;
  budgetLabel: string;
  /** True while the actual line ends at or above budget: the gap wash's tone. */
  ahead: boolean;
};

export function cumulativeChart(state: SessionState): CumulativeChartModel {
  const { days, actual, plan, label } = mtdCumulativeSeries(state);
  const top = Math.max(...actual, ...plan, 1);
  const step = top > 20_000 ? 10_000 : 5_000;
  const yMax = Math.ceil((top * 1.05) / step) * step;
  const yTicks = Array.from({ length: yMax / step + 1 }, (_, i) => i * step);
  const month = MONTHS[Number(productionDayOf(state.cursor).date.split('-')[1]) - 1];
  const lastIndex = days.length - 1;
  return {
    days: days.map((day, i) => (i === 0 || i === lastIndex ? `${day} ${month}` : day)),
    actual,
    budget: plan,
    yTicks,
    yMax,
    period: `${label} · dst`,
    actualLabel: `Actual ${(actual[lastIndex] ?? 0).toLocaleString('en-US')}`,
    budgetLabel: `Budget ${(plan[lastIndex] ?? 0).toLocaleString('en-US')}`,
    ahead: (actual[lastIndex] ?? 0) >= (plan[lastIndex] ?? 0),
  };
}

export type GapOptionLine = { text: string; strong?: boolean };

export type SuptGapOption = {
  id: string;
  rank: number;
  title: string;
  line: readonly GapOptionLine[];
  dependency: string;
  /** The seat that decides, where one is named (the plan revision: the GM). */
  decider?: string;
};

/** The gain's figure is the line's lead (`**44 dst/day** extra`). */
function gainParts(gain: string): GapOptionLine[] {
  const match = gain.match(/^(.*?)(\d[\d,.]*(?: extra)? dst\/day)(.*)$/);
  if (!match) return [{ text: gain }];
  return [{ text: match[1]! }, { text: match[2]!, strong: true }, { text: match[3]! }].filter((part) => part.text);
}

/** The ranked options (A's view): figure lead, probability, dependency. The
 * plan revision drops its forecast line — the figure row above carries it. */
export function suptGapOptions(state: SessionState): SuptGapOption[] {
  return gapClosingOptions(state).map((row, index) => {
    const revision = row.dependencies === 'Manager decision required';
    if (revision) {
      const [before, date] = row.probability.split(/ by (?=[^ ]+ [A-Z][a-z]{2}$)/);
      return {
        id: row.id,
        rank: index + 1,
        title: row.option,
        line: date ? [{ text: `${before} by ` }, { text: date, strong: true }] : [{ text: row.probability }],
        dependency: row.dependencies,
        decider: 'GM',
      };
    }
    return {
      id: row.id,
      rank: index + 1,
      title: row.option,
      line: [...gainParts(row.potentialGain), { text: ` · ${row.probability}` }],
      dependency: row.dependencies,
    };
  });
}

/** The Engine insight's body with each favourable amount set in ink 600 (comp `.bd b`). */
export function insightBodyParts(body: string): GapOptionLine[] {
  const glyph = CURRENCY_GLYPH.replace(/[$]/g, '\\$');
  const lead = `+${CURRENCY_GLYPH}`;
  return body
    .split(new RegExp(`(\\+${glyph}[\\d.,]+[KM]?)`))
    .filter(Boolean)
    .map((text) => (text.startsWith(lead) ? { text, strong: true } : { text }));
}
